import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, X, Link2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { isUrl } from "@/components/app/FormKit";
import { inputCls, labelCls } from "@/components/app/ui";

const OK = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/** One image: upload (stored per store) or paste a URL, with preview. */
export default function ImagePicker({ label, value, onChange, error, hint, required, aspect = "aspect-video", fit = "cover", t }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [urlErr, setUrlErr] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (!OK.includes(file.type)) return toast.error(`${file.name}: only JPG, PNG, GIF or WebP images are allowed.`);
    if (file.size > 5 * 1024 * 1024) return toast.error(`${file.name} is larger than 5 MB.`);
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/vendor/upload", fd);
      onChange(data.url);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const addUrl = () => {
    if (!isUrl(url)) return setUrlErr("Enter a valid URL starting with http:// or https://");
    setUrlErr(""); onChange(url.trim()); setUrl("");
  };

  return (
    <div>
      {label && <label className={labelCls}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>}
      {value ? (
        <div className={`relative ${aspect} rounded-lg overflow-hidden border border-slate-200 bg-slate-100 group`}>
          <img src={value} alt="" className={`w-full h-full ${fit === "contain" ? "object-contain p-3 bg-white" : "object-cover"}`} />
          <button type="button" onClick={() => onChange("")} aria-label="Remove image" className="absolute right-1.5 top-1.5 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"><X size={13} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} data-testid={t}
          className={`w-full ${aspect} rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-[12px] transition-colors ${error ? "border-red-400 bg-red-50/40 text-red-600" : "border-slate-300 text-slate-500 hover:border-blue-400 hover:bg-blue-50/50"}`}>
          {busy ? <Loader2 size={18} className="animate-spin text-blue-600" /> : <UploadCloud size={18} className="text-blue-600" />}
          <span className="font-semibold text-neutral-800">{busy ? "Uploading…" : "Upload image"}</span>
          <span>JPG, PNG, GIF or WebP · max 5 MB</span>
        </button>
      )}
      <input ref={ref} type="file" hidden accept={OK.join(",")} onChange={(e) => upload(e.target.files?.[0])} data-testid={t ? `${t}-input` : undefined} />
      {!value && (
        <div className="flex gap-1.5 mt-1.5">
          <div className="relative flex-1">
            <Link2 size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={url} onChange={(e) => { setUrl(e.target.value); setUrlErr(""); }} placeholder="…or paste an image URL" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
              className={`${inputCls} !h-8 pl-7 !text-[12px] ${urlErr ? "!border-red-500" : ""}`} />
          </div>
          <button type="button" onClick={addUrl} className="h-8 px-3 rounded-lg border border-slate-300 text-[12px] font-semibold hover:border-blue-300 hover:text-blue-600">Add</button>
        </div>
      )}
      {(urlErr || error) ? <p role="alert" className="mt-1 text-[11px] text-red-600">{urlErr || error}</p> : hint ? <p className="mt-1 text-[11px] text-slate-500">{hint}</p> : null}
    </div>
  );
}
